import { memo } from "react";
import { Box } from "@mui/material";

import Txt from "../../../../../components/typography/Txt";
import { formatAddressObjectFullAddress } from "../../../../../_src_shared/utils/functions/formatAddressObjectFullAddress";

import {
  PALETTE,
  Panel,
  DateRow,
  fmtDate,
  fmtRelativeShort,
  parseIsoMs,
} from "./_shared";

// Per-property date fields. Each entry pulls from `property[field]` and
// renders as its own DateRow with the property's address as the subtitle.
// Sorted chronologically across all properties so the card reads as one
// merged stream of property events rather than grouped per address.
const PROPERTY_DATE_FIELDS = [
  {
    field: "purchaseDate",
    what: "Purchase date",
    badgeLabel: "Purchase",
  },
  {
    field: "appraisalDate",
    what: "Appraisal date",
    badgeLabel: "Appraisal",
  },
  {
    field: "secondaryAppraisalDate",
    what: "Secondary appraisal",
    badgeLabel: "2nd Appraisal",
  },
  {
    field: "lastListedForSaleDate",
    what: "Last listed for sale",
    badgeLabel: "Listed",
  },
  {
    field: "floodCertDate",
    what: "Flood certification",
    badgeLabel: "Flood Cert",
  },
];

const PropertyDatesCard = ({ loan }) => {
  const properties = loan?.subjectProperties || [];

  const rows = [];
  properties.forEach((p, idx) => {
    const addr = p?.address
      ? formatAddressObjectFullAddress(p.address)
      : `Property ${idx + 1}`;
    PROPERTY_DATE_FIELDS.forEach(({ field, what, badgeLabel }) => {
      const raw = p?.[field];
      if (!raw) return;
      rows.push({
        key: `${p?._id || idx}-${field}`,
        sortMs: parseIsoMs(raw) ?? 0,
        when: fmtDate(raw),
        whenSub: fmtRelativeShort(raw),
        what,
        whatSub: addr,
        badgeLabel,
        badgeTone: "default",
        state: "default",
      });
    });
  });
  rows.sort((a, b) => a.sortMs - b.sortMs);

  return (
    <Panel title="Property dates" count={`${rows.length}`}>
      {rows.length === 0 ? (
        <Box sx={{ px: "22px", py: "20px" }}>
          <Txt
            sx={{
              fontSize: 12.5,
              color: PALETTE.ink3,
              fontStyle: "italic",
            }}
          >
            No property dates tracked yet.
          </Txt>
        </Box>
      ) : (
        rows.map((row, idx) => (
          <DateRow key={row.key} isFirst={idx === 0} {...row} />
        ))
      )}
    </Panel>
  );
};

export default memo(PropertyDatesCard);
